import React, { useEffect, useState } from 'react';

import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';

import {
  adminActions,
  adminDeleteCampaign,
  adminGetAllCampaigns,
  allCampaignsSelector,
  CampaignStatusType,
  ConfirmationTypes,
  HelpTypes,
  isLoadingSelector,
  MiscType,
  resetStatus,
  successSelector,
  utilResetValue,
} from '@pharmaplan/common';

import { IDynamicTableObject } from '../../DynamicTable/types';
import ConfirmationModal from '../../Modals/ConfirmationModal';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { setDialog } from '../../../reducers/dialogReducer';
import { Constants } from '../../../helpers/Constants';
import DynamicTable from '../../DynamicTable';
import useTableSort from '../../../hooks/useTableSort';
import strings from '../../../localization';
import { showSuccess } from '../Profile/Pharmacist/PharmacistViewProfile/helper';

import CampaignsHeader from './CampaignsHeader';
import { campaignHeaders, campaignRows } from './helper';

const { createCampaign, editCampaign, campaignBookings, linkedLeads } = Constants.paths.admin;

const loadingActions = [adminActions.getAllCampaigns];

interface ICampaignFilterValues {
  campaign: string;
  status: CampaignStatusType | '';
  userType: HelpTypes | MiscType;
}

const Campaigns = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const list = useAppSelector(allCampaignsSelector);
  const deleteSuccess = useAppSelector((state) =>
    successSelector([adminActions.deleteCampaign], state));

  const isLoading = useAppSelector((state) =>
    isLoadingSelector(loadingActions, state));

  const { isDesc, orderBy, order, handleSorting, resetSorting } = useTableSort();

  const [page, setPage] = useState(1);

  const fetchCampaigns = (
    values: ICampaignFilterValues,
    targetPage: number,
  ) => {
    const formattedUserType = values?.userType === MiscType.All ? '' : values?.userType;
    dispatch(
      adminGetAllCampaigns({
        page: targetPage,
        campaign: values?.campaign,
        status: values.status === '' ? undefined : values.status,
        userType: formattedUserType,
        sortBy: orderBy,
        desc: isDesc,
      }),
    );
  };

  const handleSubmit = (values: ICampaignFilterValues) => {
    setPage(1);
    fetchCampaigns(values, 1);
  };

  const formik = useFormik<ICampaignFilterValues>({
    initialValues: {
      campaign: '',
      status: '',
      userType: MiscType.All,
    },
    onSubmit: handleSubmit,
  });

  const handleCreate = () => {
    dispatch(utilResetValue(['campaignEmailTemplates']));

    navigate(createCampaign);
  };

  const handleEdit = (id: string) =>
    () => {
      navigate(editCampaign, { state: { campaignId: id } });
    };

  const deleteCampaignAction = (id: string) =>
    () => {
      dispatch(adminDeleteCampaign(id));
    };

  const handleDelete = (id: string) =>
    () => {
      dispatch(
        setDialog({
          showCloseButton: false,
          Component: (
            <ConfirmationModal
              type={ConfirmationTypes.deleteEntry}
              confirmAction={deleteCampaignAction(id)}
            />
          ),
          heading: {
            title: '',
          },
        }),
      );
    };

  const handleViewBookings = (campaignIdValue: string) =>
    () => {
      navigate(campaignBookings, { state: { campaignId: campaignIdValue } });
    };

  const handleViewLinkedLeads = (campaignIdValue:string, userType:HelpTypes) =>
    () => {
      navigate(linkedLeads, { state: { campaignId: campaignIdValue, activeTab: userType } });
    };

  const handleReset = () => {
    const formattedUserType = formik.values?.userType === MiscType.All ? '' : formik.values?.userType;

    setPage(1);
    resetSorting();
    formik.resetForm();
    dispatch(
      adminGetAllCampaigns({ page: 1, userType: formattedUserType }),
    );
  };

  const handlePagination = (_: unknown, selectedPage: number) => {
    const targetPage = selectedPage + 1;
    setPage(targetPage);
    fetchCampaigns(formik.values, targetPage);
  };

  const apiUserType = formik.values.userType === MiscType.All ? formik.values.userType : list.data?.[0]?.userType?.toString() || formik.values.userType;

  const table: IDynamicTableObject = {
    headers: campaignHeaders(apiUserType as HelpTypes),
    rows: campaignRows({
      data: list?.data,
      handleDelete,
      handleEdit,
      handleViewBookings,
      handleViewLinkedLeads,
    }),
    headerBar: [],
  };

  useEffect(() => {
    fetchCampaigns(formik.values, 1);
  }, []);

  useEffect(() => {
    if (orderBy) {
      setPage(1);
      fetchCampaigns(formik.values, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderBy, order]);

  useEffect(() => {
    if (deleteSuccess) {
      showSuccess(dispatch, strings.recordDeletedSuccessfully);
      dispatch(resetStatus([adminActions.deleteCampaign]));
      fetchCampaigns(formik.values, page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteSuccess]);

  return (
    <DynamicTable
      table={table}
      showHeader={false}
      loadSuccess={!isLoading}
      page={page}
      totalCount={list?.totalCount}
      orderBy={orderBy}
      order={order}
      handleSort={handleSorting}
      handlePagination={handlePagination}
      customHeader={(
        <CampaignsHeader
          formik={formik}
          handleReset={handleReset}
          handleCreate={handleCreate}
        />
      )}
    />
  );
};

export default Campaigns;
